<template>
    <ContentComp
        :load-state="LoadState"
        :show-as-modal="asModal"
    >
        <TabComp :tabs="tabs">
            <template #tab-general>
                <div class="container">
                    <div class="row row-cols-2">
                        <div class="col">
                            <dl class="row">
                                <dt class="col-sm-4">{{ $tm('general.device') }}</dt>
                                <dd class="col-sm-8">{{ row?.DeviceStatus?.DeviceId }}</dd>

                                <dt class="col-sm-4">{{ $tm('general.isconnected') }}</dt>
                                <dd class="col-sm-8">
                                    <IsConnectedIcon
                                        class="text-start"
                                        :value="row?.DeviceStatus == null ? null : row.DeviceStatus!.Connected"
                                    />
                                </dd>

                                <dt class="col-sm-4">{{ $tm('general.lastseen') }}</dt>
                                <dd class="col-sm-8">
                                    <DateComp
                                        format="DATETIME"
                                        :monospace="false"
                                        :value="row?.DeviceStatus?.LastSeen ?? null"
                                    />
                                </dd>
                            </dl>
                        </div>
                        <div class="col">
                            <dl class="row">
                                <dt class="col-sm-4">{{ $tm('general.event') }}</dt>
                                <dd class="col-sm-8">
                                    <DeviceStatusDisplayEvent
                                        :value="row?.Station?.EventName ?? ''"
                                        :row="row as StationDeviceStatus"
                                    />
                                </dd>

                                <dt class="col-sm-4 text-truncate">{{ $tm('general.stations') }}</dt>
                                <dd class="col-sm-8 text-truncate">{{ row?.Station?.Name }}</dd>

                                <dt class="col-sm-4 text-truncate">{{ $tm('general.activefromto') }}</dt>
                                <dd class="col-sm-8 text-truncate">
                                    <FromToHelper
                                        v-if="row?.StationAlias?.ActiveFrom && row?.StationAlias?.ActiveTo"
                                        :first="row?.StationAlias?.ActiveFrom"
                                        :second="row?.StationAlias?.ActiveTo"
                                    ></FromToHelper>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <ToolbarComp :actions="toolbarActions"> </ToolbarComp>
                <LineChart
                    ref="chart"
                    :data="computedData"
                    :fullscreen="true"
                    :markers="true"
                    type="bar"
                ></LineChart>
            </template>
            <template #tab-logs>
                <DeviceLog :deviceId="props.deviceId" />
            </template>
        </TabComp>
    </ContentComp>
</template>

<script setup lang="ts">
import { DateComp, GridComp, TabComp, ToolbarComp } from '@/components';
import { ChartDataset } from '@/components/Charts/ChartDataset';
import { LineChart, LineChartData } from '@/components/Charts';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useTaskLoader } from '@/composeables';
import { DeviceDao } from '@/dao';
import { DropdownItemButton } from '@/objects';
import { DeviceTransactionStatistics, StationDeviceStatus, StationDeviceStatusWithCount } from '@/objects/generated';
import { watch, computed, onMounted, ref } from 'vue';
import type { ILineChartExpose } from '@/components/Charts/ILineChartExpose';
import IsConnectedIcon from './IsConnectedIcon.vue';
import DeviceStatusDisplayEvent from './DeviceStatusDisplayEvent.vue';
import FromToHelper from '@/components/focuseventcard/FromToHelper.vue';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import DeviceLog from './DeviceLog.vue';

const props = defineProps<{
    deviceId: string;

    /**
     * The datarow for the device
     */
    row?: StationDeviceStatusWithCount;
}>();

const { AddLoader, LoadState } = useTaskLoader();

const chart = ref<ILineChartExpose | null>(null);

const data = ref<DeviceTransactionStatistics | null>(null);

const currentDataPeriod = ref<number>(300);

// We want to show the loadspinner as a modal after the first load
const asModal = ref<boolean>(false);

const tabs = new TabContainer();
tabs.AddTab(new TabDefinition('general', 'transactions.general', 'fa-chart-line'));
tabs.AddTab(new TabDefinition('logs', 'transactions.log', 'fa-clock-rotate-left'));

// Define the buttons we want in the toolbar
const toolbarActions = computed(() => [
    // Hide devices with no event
    DropdownItemButton.create({
        buttonType: 'Secondary',
        title: 'general.5minutes',
        translationkey: 'general.5minutesshort',
        class: currentDataPeriod.value === 300 ? 'active' : '',
        action: () => {
            currentDataPeriod.value = 300;
        },
    }),

    DropdownItemButton.create({
        buttonType: 'Secondary',
        title: 'general.1hour',
        translationkey: 'general.1hourshort',
        class: currentDataPeriod.value === 3600 ? 'active' : '',
        action: () => {
            currentDataPeriod.value = 3600;
        },
    }),

    DropdownItemButton.create({
        buttonType: 'Secondary',
        title: 'general.6hours',
        translationkey: 'general.6hoursshort',
        class: currentDataPeriod.value === 21600 ? 'active' : '',
        action: () => {
            currentDataPeriod.value = 21600;
        },
    }),

    DropdownItemButton.create({
        buttonType: 'Secondary',
        title: 'general.12hours',
        translationkey: 'general.12hoursshort',
        class: currentDataPeriod.value === 43200 ? 'active' : '',
        action: () => {
            currentDataPeriod.value = 43200;
        },
    }),

    DropdownItemButton.create({
        buttonType: 'Secondary',
        title: 'general.24hours',
        translationkey: 'general.24hoursshort',
        class: currentDataPeriod.value === 86400 ? 'active' : '',
        action: () => {
            currentDataPeriod.value = 86400;
        },
    }),
]);

const computedData = computed(() => {
    // We want to convert the data to LineChartData<Date>
    let _data = new LineChartData<Date>({ title: '', subtitle: undefined, datasets: [] });

    if (!data.value) {
        return _data;
    }

    let otherset = new ChartDataset<Date>({ legend: 'devicestatistics.others', xaxis: [], yaxis: [] });
    for (const [key, value] of Object.entries(data.value.Others ?? {})) {
        otherset.yaxis.push(value);
        otherset.xaxis.push(new Date(key));
    }

    let timeset = new ChartDataset<Date>({ legend: 'devicestatistics.times', xaxis: [], yaxis: [] });
    for (const [key, value] of Object.entries(data.value.Time ?? {})) {
        timeset.yaxis.push(value);
        timeset.xaxis.push(new Date(key));
    }

    let statusset = new ChartDataset<Date>({ legend: 'devicestatistics.status', xaxis: [], yaxis: [] });
    for (const [key, value] of Object.entries(data.value.Status ?? {})) {
        statusset.yaxis.push(value);
        statusset.xaxis.push(new Date(key));
    }

    _data.datasets.push(otherset);
    _data.datasets.push(timeset);
    _data.datasets.push(statusset);

    return _data;
});

const loadData = async () => {
    AddLoader(async () => {
        // Load the data
        chart.value?.Reset();
        data.value = await DeviceDao.GetTransactionsForDevice(props.deviceId, currentDataPeriod.value);
        asModal.value = true;
    }, 'devicestatistics.loading');
};

watch(currentDataPeriod, loadData);

onMounted(() => {
    loadData();
});
</script>
